import { useNavigate } from "react-router";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ArrowRight, Boxes } from "lucide-react";
import { cn } from "@/lib/cn";
import { Avatar } from "@/components/Avatar";
import type { MembroDoDia } from "./ProfissionalDoDiaCard";
import { useEmOutraOrganizacao } from "@/components/organization/painel/OrganizacaoEmFoco";

export type OperacaoLabDoMembro = {
  emProducao: number;
  emAberto: number;
  concluidos: number;
  prazoMaisProximo: { data: string | Date; paciente: string } | null;
};

/**
 * O colaborador do laboratório: o que está sob a responsabilidade dele agora.
 *
 * O cartão da clínica não serve aqui — laboratório não tem consulta, e mostrar "0 hoje / 0 no mês"
 * pra um técnico seria métrica de outro negócio. O equivalente é a prótese: quantos serviços estão
 * atribuídos a ele, quantos já saíram, e qual prazo aperta primeiro.
 *
 * Não existe "concluídos hoje": `ProsthesisService` não carimba quando foi concluído, e inventar a
 * data a partir de outro campo daria um número que não é verdade.
 */
export function ColaboradorDoLabCard({
  membro,
  operacao,
  onAbrir,
}: {
  membro: MembroDoDia;
  operacao?: OperacaoLabDoMembro;
  onAbrir?: () => void;
}) {
  const navigate = useNavigate();
  const emOutraOrganizacao = useEmOutraOrganizacao();
  const emAberto = operacao?.emAberto ?? 0;
  const situacao = membro.suspenso
    ? { rotulo: "Acesso suspenso", cor: "#dc2626", fundo: "#fef2f2" }
    : emAberto > 0
      ? { rotulo: `${emAberto} em andamento`, cor: "#f59e0b", fundo: "#fffbeb" }
      : { rotulo: "Sem trabalhos atribuídos", cor: "#71717a", fundo: "#f4f4f5" };
  const prazo = operacao?.prazoMaisProximo;

  return (
    <div
      role={onAbrir ? "button" : undefined}
      tabIndex={onAbrir ? 0 : undefined}
      onClick={onAbrir}
      onKeyDown={(e) => {
        if (!onAbrir) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onAbrir();
        }
      }}
      aria-label={onAbrir ? `Abrir perfil de ${membro.nome}` : undefined}
      className={cn(
        "flex h-[188px] flex-col gap-3 rounded-xl border border-zinc-200 bg-white p-4 dark:border-border dark:bg-card",
        membro.suspenso && "opacity-70",
        onAbrir &&
          "cursor-pointer transition-colors hover:border-zinc-300 hover:bg-zinc-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:hover:border-border dark:hover:bg-white/5",
      )}
    >
      <div className="flex items-start gap-3">
        <Avatar src={membro.avatarUrl} name={membro.nome} size="md" className="shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-zinc-900 dark:text-foreground">{membro.nome}</p>
          <p className="truncate text-xs text-zinc-500 dark:text-muted-foreground">{membro.funcao}</p>
          <p className="mt-0.5 truncate text-[11px] text-zinc-500 dark:text-muted-foreground">
            {membro.responsabilidades > 0
              ? `${membro.responsabilidades} ${membro.responsabilidades === 1 ? "responsabilidade atribuída" : "responsabilidades atribuídas"}`
              : "Sem responsabilidades atribuídas"}
          </p>
        </div>
        <span
          className="max-w-[46%] shrink-0 rounded-lg px-2 py-0.5 text-right text-[10px] font-bold uppercase leading-tight tracking-wide"
          style={{ backgroundColor: situacao.fundo, color: situacao.cor }}
        >
          {situacao.rotulo}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Numero valor={operacao?.emProducao ?? 0} rotulo="em produção" />
        <Numero valor={emAberto} rotulo="em aberto" />
        <Numero valor={operacao?.concluidos ?? 0} rotulo="concluídos" />
      </div>

      <div className="mt-auto flex items-end justify-between gap-2">
        <p className="min-w-0 flex-1 truncate text-[11px] text-zinc-500 dark:text-muted-foreground">
          {prazo
            ? `Prazo mais próximo: ${format(new Date(prazo.data), "dd 'de' MMM", { locale: ptBR })} · ${prazo.paciente}`
            : "Sem prazo em aberto"}
        </p>
        {emAberto > 0 && !emOutraOrganizacao && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/lab/producao?responsavel=${membro.userId}`);
            }}
            aria-label={`Ver a produção de ${membro.nome}`}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-zinc-200 px-2.5 text-[11px] font-semibold text-zinc-700 transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-border dark:text-foreground dark:hover:bg-primary/10"
          >
            <Boxes className="h-3.5 w-3.5" />
            Ver produção
            <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}

function Numero({ valor, rotulo }: { valor: number; rotulo: string }) {
  const vazio = valor === 0;
  return (
    <div
      className={cn(
        "rounded-lg px-2 py-1.5 text-center",
        vazio ? "bg-zinc-50/60 dark:bg-muted/20" : "bg-zinc-50 dark:bg-muted/40",
      )}
    >
      <p
        className={cn(
          "text-sm font-bold leading-tight tabular-nums",
          vazio ? "text-zinc-300 dark:text-muted-foreground/40" : "text-zinc-900 dark:text-foreground",
        )}
      >
        {valor}
      </p>
      <p
        className={cn(
          "text-[10px] leading-tight",
          vazio ? "text-zinc-400 dark:text-muted-foreground/50" : "text-zinc-500 dark:text-muted-foreground",
        )}
      >
        {rotulo}
      </p>
    </div>
  );
}
